<template>
  <div class="dash">
    <!-- 欢迎条：第一屏回答「我今天要做什么」 -->
    <section class="dash-hero">
      <div class="dash-hero__left">
        <h1 class="dash-hero__title">{{ greeting }}，{{ displayName }}</h1>
        <p class="dash-hero__sub">
          <span>{{ todayText }}</span>
          <span class="sep">·</span>
          <span>在投门店 <b class="num">{{ loading ? '—' : storeCount }}</b> 家</span>
          <span class="sep">·</span>
          <span :class="{ 'is-alert': pendingCount > 0 }">
            待处理待办 <b class="num">{{ loading ? '—' : pendingCount }}</b> 项
          </span>
          <span v-if="highRiskCount > 0" class="sep">·</span>
          <span v-if="highRiskCount > 0" class="is-alert">
            高风险 <b class="num">{{ highRiskCount }}</b> 项
          </span>
        </p>
      </div>
      <div class="dash-hero__right">
        <span class="tag" :class="scopeTagClass">{{ scopeLabel }}</span>
        <button class="btn" :disabled="loading" @click="refreshAll">刷新</button>
        <button class="btn" @click="go('/afterInvest/assetRisk')">资产风险</button>
        <button class="btn btn-primary" @click="go('/afterInvest/todo')">投后待办</button>
      </div>
    </section>

    <!-- KPI -->
    <section class="kpi-grid">
      <div
        v-for="k in kpis"
        :key="k.label"
        class="kpi"
        :class="{ 'is-alert': k.alert }"
      >
        <div class="kpi__label">{{ k.label }}</div>
        <div class="kpi__value num" :class="k.tone">{{ loading ? '—' : k.value }}</div>
        <div class="kpi__sub">{{ k.sub }}</div>
      </div>
    </section>

    <!-- 主区：待办（主）+ 风险/分布（辅） -->
    <section class="dash-main">
      <div class="card dash-todo">
        <div class="card__head">
          <span class="card__title">待办事项</span>
          <span class="card__meta">高风险优先 · 仅显示未完成的 {{ topTodos.length }} / {{ pendingCount }} 项</span>
          <button class="link" @click="go('/afterInvest/todo')">查看全部 →</button>
        </div>

        <ul v-if="topTodos.length" class="todo-list">
          <li
            v-for="t in topTodos"
            :key="t.id"
            class="todo"
            :class="riskLevel(t.riskType)"
            @click="go('/afterInvest/todo')"
          >
            <div class="todo__main">
              <div class="todo__title">
                {{ t.brandName || '—' }}
                <span class="todo__risk">{{ riskTypeMap[t.riskType] || '其他' }}</span>
              </div>
              <div class="todo__sub">
                <span class="num">{{ t.agreementNo || '—' }}</span>
                <span class="sep">·</span>
                <span>{{ t.todoType || '待办' }}</span>
              </div>
            </div>
            <div class="todo__right">
              <span class="tag" :class="statusTag(t.status)">{{ statusMap[t.status] || '—' }}</span>
              <span class="todo__time num">{{ shortTime(t.createTime) }}</span>
            </div>
          </li>
        </ul>

        <div v-else class="empty">
          <p class="empty__title">{{ loading ? '加载中…' : '暂无未完成待办' }}</p>
          <p class="empty__sub">投后异常会在此自动生成待办，也可人工发起</p>
        </div>
      </div>

      <div class="dash-side">
        <div class="card">
          <div class="card__head">
            <span class="card__title">风险类型分布</span>
            <span class="card__meta">未完成</span>
          </div>
          <div v-if="riskGroups.length" class="bar-list">
            <div v-for="r in riskGroups" :key="r.type" class="bar-row">
              <span class="bar-row__label">{{ r.label }}</span>
              <span class="bar-row__track">
                <span class="bar-row__fill" :style="{ width: r.pct + '%', background: r.color }"></span>
              </span>
              <span class="bar-row__value num">{{ r.count }}</span>
            </div>
          </div>
          <div v-else class="empty empty--sm">
            <p class="empty__title">{{ loading ? '加载中…' : '暂无风险待办' }}</p>
          </div>
          <button class="link link--block" @click="go('/afterInvest/assetRisk')">查看营业额与回款 →</button>
        </div>

        <div class="card">
          <div class="card__head">
            <span class="card__title">门店分布 Top 5</span>
            <span class="card__meta">按省份</span>
          </div>
          <div v-if="topProvinces.length" class="rank-list">
            <div v-for="(p, i) in topProvinces" :key="p.province" class="rank-row">
              <span class="rank-row__no num">{{ i + 1 }}</span>
              <span class="rank-row__name">{{ p.province }}</span>
              <span class="rank-row__value num">{{ p.count }}</span>
            </div>
          </div>
          <div v-else class="empty empty--sm">
            <p class="empty__title">{{ loading ? '加载中…' : '暂无分布数据' }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- 地图 -->
    <section class="card dash-map">
      <div class="card__head">
        <span class="card__title">全国已投门店分布</span>
        <span class="card__meta">共 <b class="num">{{ distribution.length }}</b> 个省份有门店</span>
        <button class="link" @click="go('/business/store')">门店列表 →</button>
      </div>
      <div ref="mapRef" class="dash-map__canvas"></div>
    </section>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import * as echarts from 'echarts'
import store from '@/store'
import dashboardService from '@/services/dashboardService'
import assetService from '@/services/assetService'
import postInvestService from '@/services/postInvestService'
import chinaGeo from '@/assets/maps/china.json'

const router = useRouter()

const loading = ref(false)
const stats = ref({})
const storeCount = ref(0)
const todoList = ref([])
const distribution = ref([])
const monthTotals = ref(null)
const mapRef = ref(null)
let chart = null

const statusMap = { 0: '待处理', 1: '处理中', 2: '已完成', 3: '已撤销' }
const riskTypeMap = {
  1: '友好协商回购', 2: '亏损闭店', 3: '欠款',
  4: '数据未传输', 5: '违约闭店', 6: '隐匿收入', 7: '其他'
}
const HIGH_RISK = [3, 5, 6]
const MID_RISK = [2, 4]

/* ---------- 头部问候 ---------- */
const displayName = computed(() => store.state.userInfo?.realName || store.state.userInfo?.username || '')
const greeting = computed(() => {
  const h = new Date().getHours()
  if (h < 6) return '凌晨好'
  if (h < 11) return '早上好'
  if (h < 14) return '中午好'
  if (h < 18) return '下午好'
  return '晚上好'
})
const todayText = computed(() => {
  const d = new Date()
  const week = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'][d.getDay()]
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日 ${week}`
})

/* ---------- 格式化 ---------- */
const toNumber = (v) => {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}
const formatMoney = (v) => {
  if (v === null || v === undefined || v === '') return '—'
  const n = Number(v)
  if (!Number.isFinite(n)) return '—'
  return `¥${n.toLocaleString('zh-CN', { maximumFractionDigits: 0 })}`
}
const formatPercent = (v) => {
  if (v === null || v === undefined || v === '') return '—'
  const n = parseFloat(String(v))
  if (Number.isNaN(n)) return '—'
  return `${(n * 100).toFixed(1)}%`
}
const shortTime = (t) => (t ? String(t).slice(5, 16) : '—')

/* ---------- 派生数据 ---------- */
const investAmount = computed(() => toNumber(stats.value.totalInvestAmount))
const recoveredAmount = computed(() => toNumber(stats.value.totalRecoveredAmount))
const recoveryRate = computed(() =>
  investAmount.value > 0 ? (recoveredAmount.value / investAmount.value) * 100 : null
)

const pendingTodos = computed(() =>
  todoList.value.filter(t => Number(t.status) === 0 || Number(t.status) === 1)
)
const pendingCount = computed(() => pendingTodos.value.length)
const highRiskCount = computed(() =>
  pendingTodos.value.filter(t => HIGH_RISK.includes(Number(t.riskType))).length
)

const riskLevel = (riskType) => {
  const v = Number(riskType)
  if (HIGH_RISK.includes(v)) return 'is-high'
  if (MID_RISK.includes(v)) return 'is-mid'
  return 'is-low'
}
const statusTag = (status) => ({
  0: 'tag-warning', 1: 'tag-info', 2: 'tag-success', 3: 'tag-neutral'
}[Number(status)] || 'tag-neutral')

// 排序：高风险 → 中风险 → 其他，同级按创建时间倒序
const RISK_WEIGHT = { 3: 0, 5: 0, 6: 0, 2: 1, 4: 1 }
const topTodos = computed(() =>
  [...pendingTodos.value]
    .sort((a, b) => {
      const wa = RISK_WEIGHT[Number(a.riskType)] ?? 2
      const wb = RISK_WEIGHT[Number(b.riskType)] ?? 2
      if (wa !== wb) return wa - wb
      return String(b.createTime || '').localeCompare(String(a.createTime || ''))
    })
    .slice(0, 8)
)

const riskGroups = computed(() => {
  const map = new Map()
  pendingTodos.value.forEach(t => {
    const k = Number(t.riskType)
    map.set(k, (map.get(k) || 0) + 1)
  })
  const total = pendingCount.value || 1
  return [...map.entries()]
    .map(([type, count]) => ({
      type,
      count,
      label: riskTypeMap[type] || '其他',
      pct: Math.round((count / total) * 100),
      color: HIGH_RISK.includes(type)
        ? 'var(--danger-fill)'
        : (MID_RISK.includes(type) ? 'var(--warning-fill)' : 'var(--neutral-300)')
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 6)
})

const topProvinces = computed(() =>
  [...distribution.value]
    .map(d => ({ province: d.province, count: toNumber(d.count) }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5)
)

/* ---------- 当前视角：与投资人驾驶舱保持同一套标识 ---------- */
// platform = 平台方机构（编号 1）看全量；tenant = 本方；all = 无租户上下文兜底
const isPlatform = computed(() => stats.value.scope === 'platform')

const scopeLabel = computed(() => {
  if (stats.value.scope === 'platform') return '平台方视角 · 全部机构'
  if (stats.value.scope === 'all') return '未识别机构 · 全量'
  return '本方视角'
})

const scopeTagClass = computed(() => {
  if (stats.value.scope === 'platform') return 'tag-info'
  if (stats.value.scope === 'all') return 'tag-warning'
  return 'tag-neutral'
})

const kpis = computed(() => [
  {
    label: '在投门店',
    value: storeCount.value,
    // 机构数只对平台方有意义；单一出资方口径下强调「本方」
    sub: isPlatform.value
      ? `涉及机构 ${stats.value.institutionCount ?? '—'} 家`
      : '本方口径'
  },
  {
    label: '累计投资',
    value: formatMoney(stats.value.totalInvestAmount),
    sub: `已回收 ${formatMoney(stats.value.totalRecoveredAmount)}` +
      (recoveryRate.value === null ? '' : ` · ${recoveryRate.value.toFixed(1)}%`)
  },
  {
    label: '投后营业额偏差',
    value: formatPercent(stats.value.turnoverDeviationPercent),
    tone: toNumber(stats.value.turnoverDeviationPercent) < 0 ? 'is-down' : 'is-up',
    sub: '相对投前预估'
  },
  {
    label: '本月应收',
    value: monthTotals.value ? formatMoney(monthTotals.value.receivable) : '—',
    tone: toNumber(monthTotals.value?.arrears) > 0 ? 'is-down' : '',
    alert: toNumber(monthTotals.value?.arrears) > 0,
    sub: monthTotals.value
      ? `欠款 ${formatMoney(monthTotals.value.arrears)}`
      : '未获取到本月回款数据'
  }
])

/* ---------- 取数 ---------- */
const fetchStats = async () => {
  try {
    const data = await dashboardService.getStats()
    stats.value = data || {}
    distribution.value = data?.storeDistribution || []
    renderMap()
  } catch (e) {
    console.error('获取仪表盘数据失败', e)
  }
}

const fetchStoreCount = async () => {
  try {
    const res = await assetService.getStoreList({ pageNum: 1, pageSize: 1 })
    storeCount.value = toNumber(res?.data?.size)
  } catch (e) {
    console.error('获取门店数量失败', e)
  }
}

const fetchTodos = async () => {
  try {
    const res = await postInvestService.getTodoList({ tenantId: store.state.tenantId })
    todoList.value = Array.isArray(res?.data) ? res.data : (res?.data?.list || [])
  } catch (e) {
    console.error('获取待办列表失败', e)
  }
}

const fetchMonthRecovery = async () => {
  try {
    const now = new Date()
    const pad = (n) => String(n).padStart(2, '0')
    const start = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-01`
    const end = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
    const res = await postInvestService.recoveryTable({
      brandCode: '',
      storeEntCodes: [],
      startDate: start,
      endDate: end,
      granularity: 'month'
    })
    monthTotals.value = {
      receivable: toNumber(res?.data?.totalReceivableAmount),
      arrears: toNumber(res?.data?.totalArrearsAmount),
      turnover: toNumber(res?.data?.totalTurnover)
    }
  } catch (e) {
    monthTotals.value = null // 本月回款取不到时降级为「—」，不影响首屏其余模块
  }
}

/* ---------- 地图 ---------- */
const renderMap = async () => {
  await nextTick()
  if (!mapRef.value) return
  try {
    echarts.registerMap('china', chinaGeo)
  } catch (e) { /* 已注册则忽略 */ }
  if (!chart) chart = echarts.init(mapRef.value)
  const seriesData = distribution.value.map(d => ({ name: d.province, value: toNumber(d.count) }))
  chart.setOption({
    tooltip: {
      trigger: 'item',
      formatter: (p) => `${p.name || '—'}<br/>门店 ${p.value ?? 0} 家`
    },
    visualMap: {
      left: 12,
      bottom: 12,
      min: 0,
      max: Math.max(...seriesData.map(s => s.value), 10),
      inRange: { color: ['#EEF3FE', '#DCE6FD', '#B9CDFB', '#7A9BF2', '#2C5BE8'] },
      text: ['高', '低'],
      textStyle: { color: '#5A6072', fontSize: 11 }
    },
    series: [
      {
        name: '门店数量',
        type: 'map',
        map: 'china',
        roam: true,
        zoom: 1.15,
        label: { show: false },
        itemStyle: { borderColor: '#FFFFFF', borderWidth: 0.6 },
        emphasis: { label: { show: true, color: '#141822', fontSize: 11 }, itemStyle: { areaColor: '#B9CDFB' } },
        data: seriesData
      }
    ]
  })
}

const onResize = () => chart && chart.resize()

const go = (path) => router.push(path)

const refreshAll = async () => {
  loading.value = true
  try {
    await Promise.all([fetchStats(), fetchStoreCount(), fetchTodos(), fetchMonthRecovery()])
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  window.addEventListener('resize', onResize)
  refreshAll()
})

onUnmounted(() => {
  window.removeEventListener('resize', onResize)
  if (chart) {
    chart.dispose()
    chart = null
  }
})
</script>

<style scoped>
.dash {
  width: 100%;
}

/* ---------- 欢迎条 ---------- */
.dash-hero {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: var(--space-4);
  padding: var(--space-5) var(--space-6);
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  box-shadow: var(--shadow-sm);
}

.dash-hero__title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  letter-spacing: .2px;
}

.dash-hero__sub {
  margin: 6px 0 0;
  font-size: 13px;
  color: var(--text-secondary);
}

.dash-hero__sub .sep {
  margin: 0 8px;
  color: var(--text-disabled);
}

.dash-hero__sub .is-alert {
  color: var(--danger-text);
}

.dash-hero__right {
  display: flex;
  gap: var(--space-2);
  flex-shrink: 0;
}

/* ---------- KPI ---------- */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--space-4);
  margin-top: var(--space-4);
}

.kpi {
  padding: var(--space-4) var(--space-5);
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  box-shadow: var(--shadow-sm);
  transition: box-shadow var(--dur-fast, .15s) ease;
}

.kpi:hover {
  box-shadow: var(--shadow-md);
}

.kpi.is-alert {
  border-color: var(--danger-fill);
  background: linear-gradient(180deg, #FFF 0%, var(--danger-bg) 100%);
}

.kpi__label {
  font-size: 13px;
  color: var(--text-secondary);
}

.kpi__value {
  margin-top: 8px;
  font-size: 26px;
  font-weight: 600;
  line-height: 1.2;
  color: var(--text-primary);
}

.kpi__value.is-up { color: var(--up); }
.kpi__value.is-down { color: var(--down); }

.kpi__sub {
  margin-top: 6px;
  font-size: 12px;
  color: var(--text-tertiary);
}

/* ---------- 卡片通用 ---------- */
.card {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  box-shadow: var(--shadow-sm);
}

.card__head {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: 14px var(--space-5);
  border-bottom: 1px solid var(--border);
}

.card__title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.card__meta {
  font-size: 12px;
  color: var(--text-tertiary);
}

.link {
  margin-left: auto;
  padding: 0;
  border: none;
  background: none;
  font-size: 12px;
  color: var(--info-text);
  cursor: pointer;
}

.link:hover {
  color: var(--brand-600);
}

.link--block {
  display: block;
  margin: 12px var(--space-5) 14px;
  text-align: left;
}

/* ---------- 主区 ---------- */
.dash-main {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
  gap: var(--space-4);
  margin-top: var(--space-4);
}

.dash-side {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

/* 待办列表 */
.todo-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.todo {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
  padding: 12px var(--space-5);
  border-left: 3px solid transparent;
  border-bottom: 1px solid var(--border);
  cursor: pointer;
  transition: background var(--dur-fast, .15s) ease;
}

.todo:last-child {
  border-bottom: none;
}

.todo:hover {
  background: var(--bg-hover);
}

.todo.is-high { border-left-color: var(--danger-fill); }
.todo.is-mid { border-left-color: var(--warning-fill); }
.todo.is-low { border-left-color: var(--neutral-300); }

.todo__title {
  font-size: 13px;
  font-weight: 500;
  color: var(--text-primary);
}

.todo__risk {
  margin-left: 8px;
  font-size: 12px;
  font-weight: 400;
  color: var(--text-secondary);
}

.todo__sub {
  margin-top: 4px;
  font-size: 12px;
  color: var(--text-tertiary);
}

.todo__sub .sep {
  margin: 0 6px;
}

.todo__right {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  flex-shrink: 0;
}

.todo__time {
  font-size: 12px;
  color: var(--text-tertiary);
  min-width: 76px;
  text-align: right;
}

/* 风险分布条 */
.bar-list {
  padding: 14px var(--space-5) 4px;
}

.bar-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.bar-row__label {
  width: 84px;
  flex-shrink: 0;
  font-size: 12px;
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.bar-row__track {
  flex: 1;
  height: 8px;
  background: var(--neutral-50);
  border-radius: 999px;
  overflow: hidden;
}

.bar-row__fill {
  display: block;
  height: 100%;
  border-radius: 999px;
  transition: width .3s ease;
}

.bar-row__value {
  width: 28px;
  text-align: right;
  font-size: 12px;
  color: var(--text-primary);
}

/* 省份排行 */
.rank-list {
  padding: 10px var(--space-5) 14px;
}

.rank-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 0;
  font-size: 13px;
}

.rank-row__no {
  width: 18px;
  height: 18px;
  line-height: 18px;
  text-align: center;
  font-size: 11px;
  color: var(--text-tertiary);
  background: var(--neutral-50);
  border-radius: var(--radius-tag);
}

.rank-row__name {
  flex: 1;
  color: var(--text-primary);
}

.rank-row__value {
  color: var(--text-secondary);
}

/* 空状态 */
.empty {
  padding: 40px 20px;
  text-align: center;
}

.empty--sm {
  padding: 28px 20px;
}

.empty__title {
  margin: 0;
  font-size: 13px;
  color: var(--text-secondary);
}

.empty__sub {
  margin: 6px 0 0;
  font-size: 12px;
  color: var(--text-tertiary);
}

/* ---------- 地图 ---------- */
.dash-map {
  margin-top: var(--space-4);
}

.dash-map__canvas {
  height: 420px;
  padding: 8px 0;
}

/* ---------- 响应式 ---------- */
@media (max-width: 1440px) {
  .kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 1100px) {
  .dash-main {
    grid-template-columns: minmax(0, 1fr);
  }

  .dash-hero {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
